/**
 * The detail pane: whichever plan the trainer is pointed at, read-only.
 *
 * ── One prop, and the pane owns everything else ──────────────────────────────────────────────
 * `subject` is what the list selected — a template or a client's plan — or `null`, which is the
 * empty list and its own state. The two reads, the tick, the skeleton gate and the strip's expansion
 * are all held here, so a caller adds this pane to a screen by handing it a subject. That is the
 * whole interface.
 *
 * A DISCRIMINATED subject rather than a row and a flag: the branch that narrows the type is the same
 * branch that decides the copy, so a caller cannot hand over a template and claim it is a client's
 * plan. See `model/inspectorSubject`.
 *
 * ── The sections below the hero always RESERVE ───────────────────────────────────────────────
 * The week and the muscle split hold `--h-pl-week` and `--h-pl-muscle` from the first frame and show
 * bars inside their reserve, gated — the gate hides the bars, never the box. What the HERO does and
 * does not wait for differs between the two subjects and is `InspectorHero`'s own header.
 *
 * ── The swap is keyed on the PLAN, and it is SILENT until the trainer causes one ─────────────
 * The canon's own note — "podmiana inspektora jedzie LICZNIKIEM, nie indeksem" — is about the
 * specimen's CSS: it alternates two identical `@keyframes` because a jump of two rows would not
 * change the animation's NAME and the browser would not replay it. React has no such problem. It
 * remounts on any changed `key`, and a plan id is unique per row, so every jump replays the fade
 * and a return to a plan the trainer has just left replays it too.
 *
 * What it DID replay, and must not, is the list landing. The key was `planId ?? 'none'`, which holds
 * `'none'` while the library is on its way and takes the first plan's id the moment it arrives — so
 * the pane played its swap on top of the hand-over the shape was already performing, and the trainer
 * saw the same content enter twice. The key is now the LIST's wait while there is one, and the step
 * comes from `useMotionOnKeyChange`, which suppresses `initial` until the key has actually been
 * exchanged. Crossing `null` is an arrival or a departure, never an exchange; moving the pointer is
 * one, and still plays.
 *
 * This held two `useState`s and a render-phase write to keep a counter that incremented exactly
 * when `planId` changed — a second copy of the selection, kept in step by hand, defended by a
 * paragraph about `@keyframes` names that the sentence after it contradicted.
 *
 * ── The CARD's chrome does not fade, and the canon's does ────────────────────────────────────
 * `paneRightD` in the specimen puts the swap animation on the card itself, so its border, its
 * radius and its shadow fade with the content. Here the animation is on the content wrapper
 * inside it. That is a deliberate difference: a box that reserves its size before its data arrives
 * is this repository's first loading rule, and a card whose own outline fades in reads as a card
 * that was not there a moment ago. The 1px hairline and the shadow are what stops moving; nothing
 * a trainer is reading changes.
 */

import { motion } from 'motion/react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import {
  screenMotion,
  useMotionOnKeyChange,
  useScreenMotionPreference,
} from '~/shared/design-system/motion';
import { PatternWeek } from '~/entities/plan-draft';
import { useSkeletonGate, type SkeletonGate } from '~/shared/lib/useSkeletonGate';
import hidden from '~/shared/ui/visuallyHidden.module.css';

import { planCategoryColour } from '~/shared/ui/planCategoryColour';

import { templateFollowersQueryOptions } from '../api/assignmentQueries';
import { inspectorQueryOptions } from '../api/inspectorQueries';
import type { InspectorSubject } from '../model/inspectorSubject';
import type { InspectorAbsence } from '../model/planListStatus';
import type { PersonView } from '../model/templateFollowers';
import { AssignedStrip, ClientStrip, type StripFaces } from './AssignedStrip';
import { InspectorEmpty } from './InspectorEmpty';
import { InspectorHero } from './InspectorHero';
import { MuscleSection } from './MuscleSection';
import scrollbar from '~/shared/ui/scrollbar.module.css';
import styles from './Inspector.module.css';
import { useContentLanguage } from '~/shared/i18n/contentLanguage';

interface InspectorProps {
  /** What the list is pointed at, or `null` when the list has no rows to point at. */
  readonly subject: InspectorSubject | null;
  /**
   * Why there is no row: the list's own read status, or which kind of nothing it answered with.
   * Decides whether the empty pane carries a sentence at all — see `InspectorEmpty`.
   */
  readonly absence: InspectorAbsence;
  /**
   * The LIST's wait, not this pane's. Two things depend on it and neither is about the pane's own
   * read: the swap must not play when the list hands over its first plan, and the standing shape
   * must draw no bars inside the gate's 150ms grace.
   */
  readonly listGate: SkeletonGate;
  /** The pane's BOX, which the screen owns: both panes are cut from one `.card`. */
  readonly className?: string;
}

/**
 * The follower read, as the strip's three states.
 *
 * A template the map has no entry for followed NOBODY, which is `ready` with an empty list rather
 * than a wait: the map is the whole answer for every template in one read.
 */
function facesOf(
  read: { isError: boolean; data?: ReadonlyMap<number, readonly PersonView[]> },
  templateId: number,
): StripFaces {
  if (read.isError) return { status: 'unavailable' };
  if (read.data === undefined) return { status: 'pending' };
  return { status: 'ready', people: read.data.get(templateId) ?? [] };
}

export function Inspector({ subject, absence, listGate, className }: InspectorProps) {
  const { t, i18n } = useTranslation();
  const contentLang = useContentLanguage();
  const reducedMotion = useScreenMotionPreference();

  const planId = subject?.planId ?? null;

  // `enabled` rather than a branch above the hook: the pane keeps its geometry when there is no
  // row, and a conditional hook is not a thing React allows anyway.
  const plan = useQuery({
    ...inspectorQueryOptions(planId ?? 0, contentLang, (key) => t(key)),
    enabled: subject !== null,
  });
  // Only a TEMPLATE's strip needs the map. A client's plan already carries its one person on the row
  // the trainer pointed at, so the strip beside it waits for nothing — and the observer is the
  // screen's own list read in that scope anyway, which is the same cache entry either way.
  const followers = useQuery({
    // The reader's locale: this read carries no `lang` and the parameter is only the collator that
    // orders the strip's people.
    ...templateFollowersQueryOptions(i18n.language),
    enabled: subject?.kind === 'template',
  });

  // ONE gate over the whole pane, because the week and the muscle split are one answer: a grid
  // drawn from a response the split has not been read out of yet is a partial answer wearing the
  // shape of a complete one.
  const gate = useSkeletonGate(subject !== null && plan.isPending);

  // What the pane is showing, as one key. `null` while the LIST is still waiting: arriving at the
  // first plan is not an exchange, so the swap stays silent and the hand-over below it is the only
  // thing that moves. Moving the pointer exchanges one plan for another and plays.
  const shown = listGate.waiting ? null : (planId === null ? 'none' : String(planId));
  const swap = useMotionOnKeyChange(shown, screenMotion('swap', { reducedMotion }));

  return (
    <aside
      /* The hand-over sits HERE, on the one box in this pane that stands from the first frame.
       * `.reveal` is a keyframe with `fill-mode: both`, so it replays on every remount — and
       * everything inside is keyed on the shown plan, so a pointer swap would have played the
       * hand-over again on top of `swap`. The pane holds no ink of its own (`.pane` is scrollbar
       * variables), so fading it is fading exactly what the bars handed over to. */
      className={[String(scrollbar.scrollbar), String(styles.pane), listGate.reveal, className ?? '']
        .filter(Boolean)
        .join(' ')}
      aria-label={
        subject === null
          ? t('web:planLibrary.title')
          : t('web:planLibrary.inspector.regionLabel', {
              name: subject.kind === 'template' ? subject.row.name : subject.row.planName,
            })
      }
      /* BOTH waits, because the card has two and a region reports one value: the list's, while the
       * pane stands in the shape of a plan it does not have yet, and its own `/full` read's after
       * that. Reporting only the second said settled over a pane that was still drawing the first
       * one's bars, and `InspectorEmpty` inside was answering the same question differently.
       * The wait, not the read: the skeleton sections outlive the answer by the gate's minimum
       * life, and a pane still drawing bars is still about to change (owner, 21 September 2026). */
      aria-busy={gate.waiting || listGate.waiting}
    >
      {/* The category's two locals are set HERE, not on the hero: the chip on the plate and the
        * band across each week tile are the same colour, and the tiles are the hero's SIBLING, so a
        * custom property set on the hero would not reach them. */}
      <motion.div
        key={shown ?? 'wait'}
        className={styles.content}
        style={subject === null ? undefined : planCategoryColour(subject.row.categoryKey)}
        initial={swap.initial}
        animate={swap.animate}
        transition={swap.transition}
      >
        {subject === null ? (
          <InspectorEmpty reason={absence} skeleton={listGate.skeleton} />
        ) : (
          <>
            {/* No hand-over class here: it is on the pane, which does not remount when the
              * trainer points at another plan. Its own `/full` read fills in the sub-line and two
              * figures, and that has never been a reason to fade the plate. */}
            <InspectorHero subject={subject} plan={plan.data ?? null} />
            {plan.isError ? (
              <div className={styles.failure}>
                <p className={styles.failureText}>
                  {t('web:planLibrary.inspector.errorTitle')}
                </p>
                <button
                  type="button"
                  className={styles.retry}
                  onClick={() => {
                    void plan.refetch();
                  }}
                >
                  {t('web:planLibrary.states.retry')}
                </button>
              </div>
            ) : (
              <>
                {/* `gate.waiting` rather than the read's own `isPending`: a section that swapped
                  * to its content the moment `/full` landed would flash its bars away and then
                  * blink to transparent 300ms later, when the gate finally handed out `.reveal`.
                  * `plan.data` is `undefined` on a failure too, but `plan.isError` is caught above,
                  * so the `null` reaching these two is only ever the wait. */}
                <PatternWeek
                  className={styles.week}
                  view={gate.waiting ? null : (plan.data ?? null)}
                  skeleton={gate.skeleton}
                  reveal={gate.reveal}
                />
                <MuscleSection
                  view={gate.waiting ? null : (plan.data ?? null)}
                  skeleton={gate.skeleton}
                  reveal={gate.reveal}
                />
              </>
            )}
            {subject.kind === 'template' ? (
              <AssignedStrip
                count={subject.row.assignedClientCount}
                faces={facesOf(followers, subject.row.id)}
              />
            ) : (
              <ClientStrip client={subject.row.client} />
            )}
            {/* LAST, so a node that comes and goes cannot shift the indices of the sections above
              * it. A label rather than a live region: the pane is `aria-busy` for exactly as long
              * as this sentence stands, and a reader arriving here hears what it is waiting for. */}
            {gate.waiting ? (
              <span className={hidden.srOnly}>{t('web:planLibrary.inspector.loading')}</span>
            ) : null}
          </>
        )}
      </motion.div>
    </aside>
  );
}
